import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondDocumentation,
  BondDocumentationApiItem,
  DeleteBondDocumentationResult,
  UpsertBondDocumentationRequest,
} from './bond-documentation.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondDocumentationService {
  private readonly endpoint = apiUrl('bonddocumentation');

  constructor(private readonly http: HttpClient) {}

  getBondDocumentations(): Observable<BondDocumentation[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondDocumentation(item))),
    );
  }

  createBondDocumentation(payload: UpsertBondDocumentationRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondDocumentation(id: number, payload: UpsertBondDocumentationRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondDocumentation(id: number): Observable<DeleteBondDocumentationResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondDocumentationResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondDocumentationResult>({
        kind: 'error',
        message: 'Unable to delete bond documentation at this time. Please try again.',
      })),
    );
  }

  private normalizeBondDocumentation(item: BondDocumentationApiItem): BondDocumentation {
    return {
      id: this.toNumber(item.documentationId),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondDocumentationApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): BondDocumentationApiItem[] {
    if (Array.isArray(response)) {
      return response as BondDocumentationApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondDocumentationApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondDocumentationApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
